/**
 * BHPH Dashboard Page
 * Displays key metrics, statistics, and quick actions for BHPH account management.
 */

import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import PendingExportExemptions from '../../components/deals/PendingExportExemptions';
import {
  formatCurrency,
  getCollectionStats,
  getLateFeeStats,
  getPromiseStats,
} from '../../services/bhphApi';
import PortalRequestsPanel from './components/PortalRequestsPanel';

const BHPHDashboardPage: React.FC = () => {
  const navigate = useNavigate();

  // Fetch dashboard data
  const { data: collectionStats, isLoading: loadingCollections } = useQuery({
    queryKey: ['collectionStats'],
    queryFn: getCollectionStats,
    refetchInterval: 60000, // Refresh every minute
    retry: 1,
    onError: (error) => console.error('Failed to load collection stats:', error),
  });

  const { data: promiseStats, isLoading: loadingPromises } = useQuery({
    queryKey: ['promiseStats'],
    queryFn: getPromiseStats,
    refetchInterval: 60000,
    retry: 1,
    onError: (error) => console.error('Failed to load promise stats:', error),
  });

  const { data: lateFeeStats, isLoading: loadingLateFees } = useQuery({
    queryKey: ['lateFeeStats'],
    queryFn: getLateFeeStats,
    refetchInterval: 60000,
    retry: 1,
    onError: (error) => console.error('Failed to load late fee stats:', error),
  });

  const isLoading = loadingCollections || loadingPromises || loadingLateFees;

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">BHPH Dashboard</h1>
        <p className="text-gray-600 mt-1">Account management, collections, and payment tracking</p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <button
          onClick={() => navigate('/bhph/accounts')}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-4 rounded-lg shadow transition duration-150"
        >
          📂 Manage Accounts
        </button>
        <button
          onClick={() => navigate('/bhph/worklist')}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg shadow transition duration-150"
        >
          📋 Collection Worklist
        </button>
        <button
          onClick={() => navigate('/bhph/payment')}
          className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg shadow transition duration-150"
        >
          💵 Process Payment
        </button>
        <button
          onClick={() => navigate('/bhph/promises')}
          className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 rounded-lg shadow transition duration-150"
        >
          🤝 View Promises
        </button>
        <button
          onClick={() => navigate('/bhph/late-fees')}
          className="bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-4 rounded-lg shadow transition duration-150"
        >
          ⚠️ Late Fees
        </button>
      </div>

      {/* Portal Requests panel owns its own queries (B5) -- renders
          independently of the three dashboard queries below, never gated by
          their isLoading. */}
      <PortalRequestsPanel />

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 mt-4">Loading dashboard...</p>
        </div>
      ) : (
        <>
          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Total Active Accounts */}
            <MetricCard
              title="Active Accounts"
              value={collectionStats?.total_active_accounts || 0}
              subtitle="Currently active"
              icon="👥"
              color="blue"
              onClick={() => navigate('/bhph/accounts')}
            />

            {/* Delinquent Accounts */}
            <MetricCard
              title="Delinquent Accounts"
              value={collectionStats?.delinquent_accounts || 0}
              subtitle={`${collectionStats?.delinquency_rate || 0}% of portfolio`}
              icon="⚠️"
              color="red"
              onClick={() => navigate('/bhph/worklist')}
            />

            {/* Promise Fulfillment Rate */}
            <MetricCard
              title="Promise Fulfillment"
              value={`${promiseStats?.fulfillment_rate || 0}%`}
              subtitle={`${promiseStats?.pending_count || 0} pending`}
              icon="🤝"
              color="purple"
              onClick={() => navigate('/bhph/promises')}
            />

            {/* Late Fee Revenue (This Month) */}
            <MetricCard
              title="Late Fees (This Month)"
              value={formatCurrency(lateFeeStats?.this_month_fees_amount || 0)}
              subtitle={`${lateFeeStats?.this_month_fees_count || 0} fees applied`}
              icon="💰"
              color="green"
              onClick={() => navigate('/bhph/late-fees')}
            />
          </div>

          {/* Export exemptions awaiting a Bill of Lading. NOTE these are usually CASH deals, not
              BHPH accounts -- they sit here because this is the screen Jose works from, but the
              component is deliberately standalone so it can also live on the main Dashboard. The
              liability is real: a vehicle sold tax-free that never leaves the country leaves the
              dealership owing the state the sales tax. */}
          <div className="mb-8">
            <PendingExportExemptions />
          </div>

          {/* Delinquency Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Delinquency Buckets */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Delinquency Breakdown</h2>
              {collectionStats && (
                <div className="space-y-3">
                  <DelinquencyBar
                    label="Current"
                    count={collectionStats.current_accounts}
                    total={collectionStats.total_active_accounts}
                    color="bg-green-500"
                  />
                  <DelinquencyBar
                    label="Early (1-7 days)"
                    count={collectionStats.delinquency_buckets.early_1_7_days}
                    total={collectionStats.total_active_accounts}
                    color="bg-yellow-500"
                  />
                  <DelinquencyBar
                    label="Moderate (8-15 days)"
                    count={collectionStats.delinquency_buckets.moderate_8_15_days}
                    total={collectionStats.total_active_accounts}
                    color="bg-orange-400"
                  />
                  <DelinquencyBar
                    label="Serious (16-30 days)"
                    count={collectionStats.delinquency_buckets.serious_16_30_days}
                    total={collectionStats.total_active_accounts}
                    color="bg-orange-600"
                  />
                  <DelinquencyBar
                    label="Critical (31-60 days)"
                    count={collectionStats.delinquency_buckets.critical_31_60_days}
                    total={collectionStats.total_active_accounts}
                    color="bg-red-600"
                  />
                  <DelinquencyBar
                    label="Severe (61+ days)"
                    count={collectionStats.delinquency_buckets.severe_61_plus_days}
                    total={collectionStats.total_active_accounts}
                    color="bg-purple-600"
                  />
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Delinquent Balance:</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(collectionStats?.total_delinquent_balance || 0)}
                  </span>
                </div>
              </div>
            </div>

            {/* Promise & Late Fee Summary */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Collections Summary</h2>

              {/* Promise Statistics */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Promise-to-Pay</h3>
                <div className="space-y-2">
                  <StatRow label="Total Promises" value={promiseStats?.total_promises || 0} />
                  <StatRow
                    label="Pending"
                    value={promiseStats?.pending_count || 0}
                    highlight="text-yellow-600"
                  />
                  <StatRow
                    label="Kept"
                    value={promiseStats?.kept_count || 0}
                    highlight="text-green-600"
                  />
                  <StatRow
                    label="Broken"
                    value={promiseStats?.broken_count || 0}
                    highlight="text-red-600"
                  />
                  <StatRow
                    label="Pending Amount"
                    value={formatCurrency(promiseStats?.pending_promise_amount || 0)}
                    highlight="text-blue-600"
                  />
                </div>
              </div>

              {/* Late Fee Statistics */}
              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Late Fees</h3>
                <div className="space-y-2">
                  <StatRow label="Total Applied" value={lateFeeStats?.total_fees_applied || 0} />
                  <StatRow
                    label="Total Collected"
                    value={formatCurrency(lateFeeStats?.total_amount_collected || 0)}
                    highlight="text-green-600"
                  />
                  <StatRow
                    label="Total Waived"
                    value={formatCurrency(lateFeeStats?.total_amount_waived || 0)}
                    highlight="text-gray-600"
                  />
                  <StatRow
                    label="Waiver Rate"
                    value={`${lateFeeStats?.waiver_rate || 0}%`}
                    highlight="text-orange-600"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Action Items */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Action Items</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <ActionCard
                title="Review Worklist"
                description={`${collectionStats?.delinquent_accounts || 0} accounts need attention`}
                buttonText="View Worklist"
                onClick={() => navigate('/bhph/worklist')}
                color="blue"
              />
              <ActionCard
                title="Broken Promises"
                description={`${promiseStats?.broken_count || 0} promises need follow-up`}
                buttonText="View Promises"
                onClick={() => navigate('/bhph/promises?filter=broken')}
                color="red"
              />
              <ActionCard
                title="Late Fee Config"
                description="Review or update late fee settings"
                buttonText="Manage Fees"
                onClick={() => navigate('/bhph/late-fees')}
                color="orange"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ===== Component: Metric Card =====
interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  icon: string;
  color: 'blue' | 'red' | 'green' | 'purple' | 'orange';
  onClick?: () => void;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  color,
  onClick,
}) => {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200',
    red: 'bg-red-50 border-red-200',
    green: 'bg-green-50 border-green-200',
    purple: 'bg-purple-50 border-purple-200',
    orange: 'bg-orange-50 border-orange-200',
  };

  return (
    <div
      className={`${colorClasses[color]} border-2 rounded-lg p-6 ${
        onClick ? 'cursor-pointer hover:shadow-lg transition duration-150' : ''
      }`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-2xl">{icon}</span>
        <span className="text-3xl font-bold text-gray-900">{value}</span>
      </div>
      <h3 className="text-sm font-semibold text-gray-700 mb-1">{title}</h3>
      <p className="text-xs text-gray-600">{subtitle}</p>
    </div>
  );
};

// ===== Component: Delinquency Bar =====
interface DelinquencyBarProps {
  label: string;
  count: number;
  total: number;
  color: string;
}

const DelinquencyBar: React.FC<DelinquencyBarProps> = ({ label, count, total, color }) => {
  const percentage = total > 0 ? (count / total) * 100 : 0;

  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-700">{label}</span>
        <span className="font-semibold text-gray-900">
          {count} ({percentage.toFixed(1)}%)
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`${color} h-2 rounded-full transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// ===== Component: Stat Row =====
interface StatRowProps {
  label: string;
  value: string | number;
  highlight?: string;
}

const StatRow: React.FC<StatRowProps> = ({ label, value, highlight }) => {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-600">{label}:</span>
      <span className={`font-semibold ${highlight || 'text-gray-900'}`}>{value}</span>
    </div>
  );
};

// ===== Component: Action Card =====
interface ActionCardProps {
  title: string;
  description: string;
  buttonText: string;
  onClick: () => void;
  color: 'blue' | 'red' | 'orange';
}

const ActionCard: React.FC<ActionCardProps> = ({
  title,
  description,
  buttonText,
  onClick,
  color,
}) => {
  const colorClasses = {
    blue: 'bg-blue-600 hover:bg-blue-700',
    red: 'bg-red-600 hover:bg-red-700',
    orange: 'bg-orange-600 hover:bg-orange-700',
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <h3 className="font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600 mb-4">{description}</p>
      <button
        onClick={onClick}
        className={`${colorClasses[color]} text-white font-medium py-2 px-4 rounded transition duration-150 w-full`}
      >
        {buttonText}
      </button>
    </div>
  );
};

export default BHPHDashboardPage;
